// The section emits its own <script defer> tag, so two media quote sections on one
// page load this file twice. customElements.define throws on a second definition.
if (!customElements.get('media-quotes')) {
  customElements.define(
    'media-quotes',
    class MediaQuotes extends HTMLElement {
      connectedCallback() {
        this.quotes = Array.from(this.querySelectorAll('.media-quotes__quote'));
        this.index = Math.max(0, this.quotes.findIndex((quote) => quote.classList.contains('is-active')));
        this.live = this.querySelector('[data-mq-live]');

        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.duration = this.reducedMotion ? 0 : Number(this.dataset.duration) || 0;
        this.interval = Number(this.dataset.interval) || 0;
        this.autoplay = this.hasAttribute('data-autoplay') && this.interval > 0 && !this.reducedMotion && this.quotes.length > 1;
        this.classList.toggle('no-autoplay', !this.autoplay);

        this.userPaused = false;
        this.locked = false;
        // Reasons autoplay is temporarily held: hover, focus, hidden, offscreen, editor.
        this.holds = new Set();
        if (document.hidden) this.holds.add('hidden');

        this.abort = new AbortController();
        const options = { signal: this.abort.signal };

        if (this.quotes.length > 1) this.bindControls(options);

        document.addEventListener('visibilitychange', () => this.setHold('hidden', document.hidden), options);
        this.observer = new IntersectionObserver(([entry]) => this.setHold('offscreen', !entry.isIntersecting));
        this.observer.observe(this);

        this.updatePlayback();
      }

      disconnectedCallback() {
        this.abort.abort();
        this.observer.disconnect();
        clearTimeout(this.timer);
        clearTimeout(this.lockTimer);
      }

      bindControls(options) {
        this.addEventListener(
          'click',
          (event) => {
            const target = event.target.closest('[data-mq-page], [data-mq-pause]');
            if (!target) return;
            if (target.hasAttribute('data-mq-page')) this.goTo(Number(target.dataset.mqPage), { manual: true });
            else this.togglePause(target);
          },
          options
        );

        this.addEventListener('mouseenter', () => this.setHold('hover', true), options);
        this.addEventListener('mouseleave', () => this.setHold('hover', false), options);
        // Only keyboard focus holds autoplay; a mouse click on the pause button must not.
        this.addEventListener('focusin', (event) => this.setHold('focus', event.target.matches(':focus-visible')), options);
        this.addEventListener(
          'focusout',
          (event) => {
            if (!this.contains(event.relatedTarget)) this.setHold('focus', false);
          },
          options
        );

        this.addEventListener(
          'shopify:block:select',
          (event) => {
            const index = this.quotes.indexOf(event.target.closest('.media-quotes__quote'));
            if (index < 0) return;
            this.setHold('editor', true);
            this.goTo(index, { force: true });
          },
          options
        );
        this.addEventListener('shopify:block:deselect', () => this.setHold('editor', false), options);
      }

      goTo(target, { manual = false, force = false } = {}) {
        const count = this.quotes.length;
        const next = ((target % count) + count) % count;
        if (count < 2 || next === this.index || (this.locked && !force)) return;

        const from = this.quotes[this.index];
        const to = this.quotes[next];

        // Set the direction and flush styles first, so the incoming quote starts
        // from the correct side before is-active gives it a transition.
        this.quotes.forEach((quote) => quote.classList.remove('is-leaving'));
        this.dataset.direction = target > this.index ? 'next' : 'prev';
        void this.offsetWidth;

        from.classList.remove('is-active');
        from.classList.add('is-leaving');
        from.setAttribute('aria-hidden', 'true');
        to.classList.add('is-active');
        to.setAttribute('aria-hidden', 'false');
        this.index = next;

        this.locked = true;
        clearTimeout(this.lockTimer);
        this.lockTimer = setTimeout(() => {
          from.classList.remove('is-leaving');
          this.locked = false;
        }, this.duration);

        this.querySelectorAll('[data-mq-page]').forEach((page) => {
          if (Number(page.dataset.mqPage) === next) page.setAttribute('aria-current', 'true');
          else page.removeAttribute('aria-current');
        });
        const current = this.querySelector('[data-mq-current]');
        if (current) current.textContent = next + 1;

        // Announce only quotes the visitor chose; auto-rotation must not chatter.
        if (manual && this.live) this.live.textContent = to.textContent.trim().replace(/\s+/g, ' ');

        this.updatePlayback();
      }

      togglePause(button) {
        this.userPaused = !this.userPaused;
        this.classList.toggle('is-paused', this.userPaused);
        button.setAttribute('aria-label', this.userPaused ? button.dataset.labelPlay : button.dataset.labelPause);
        this.updatePlayback();
      }

      setHold(reason, on) {
        if (on) this.holds.add(reason);
        else this.holds.delete(reason);
        this.updatePlayback();
      }

      // Restarts the timer from zero on every call.
      updatePlayback() {
        const playing = this.autoplay && !this.userPaused && this.holds.size === 0;
        this.classList.toggle('is-playing', playing);
        clearTimeout(this.timer);
        if (playing) this.timer = setTimeout(() => this.goTo(this.index + 1), this.interval);
      }
    }
  );
}